import { Box, HStack, Text } from "@chakra-ui/react"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import type { AlertCondition, AlertConditionGroup, AlertRuleState } from "@hydrologiq/shared/components/alerts"
import { MUTED, USER_DEFINED } from "@hydrologiq/shared/components/alerts/alertSummary"
import { formatChipLabel, getTimeWindowDisplayValue } from "@hydrologiq/shared/components/alerts/alertRuleFormat"
import { customAlertField, operatorWithSymbol } from "@hydrologiq/shared/components/alerts/customAlertFields"
import { ConditionGroup, conditionLines } from "./ConditionGroup"
import { COMPARISON_WIDTH, FIELD_WIDTH, LockedBox, UnitInput } from "./UnitInput"

const TIME_UNITS = { h: "h", m: "min", s: "s" } as const
const LOCKED_REASON = "Uneditable whilst alert is active"

type RowLine =
  | { kind: "clause"; condition: AlertCondition }
  | { kind: "threshold" }
  | { kind: "timeWindow" }
  | { kind: "event" }

/** A Custom Alert's conditions as the editor opens them: one condition is a group of one. */
export function savedConditions(rule: AlertRuleState): AlertConditionGroup {
  const { template, targetAssetId } = rule
  if (template.conditionGroup) return template.conditionGroup
  return {
    connective: "AND",
    conditions: [
      {
        targetAssetId: targetAssetId ?? "",
        metric: template.metric ?? "",
        operator: template.operator ?? "gt",
        threshold: template.threshold ?? 0,
      },
    ],
    groups: [],
  }
}

const fieldLabel = (metric?: string | null) => customAlertField(metric)?.label ?? formatChipLabel(metric)

/**
 * A saved alert's conditions, locked and greyed: each a field, a comparison and its
 * threshold, in the same AND/OR brackets the editor draws. Only Edit changes them, and
 * only once the alert is switched off.
 */
export function AlertConditionRows({
  rule,
  custom,
  assetNames = {},
}: {
  rule: AlertRuleState
  custom: boolean
  /** Names of the Modules conditions read, to say which one each condition is on */
  assetNames?: Record<string, string>
}) {
  const { template, assignment, isEnabled, isOverridden, effectiveThreshold } = rule
  const group = custom ? savedConditions(rule) : null
  const timeWindow = assignment?.timeWindowOverrideSeconds ?? template.timeWindowSeconds
  const window = timeWindow != null ? getTimeWindowDisplayValue(timeWindow) : null
  const unit = template.thresholdUnit ?? customAlertField(template.metric)?.unit ?? ""

  const lines = group
    ? conditionLines<RowLine>(
        group.conditions.map((condition) => ({ kind: "clause", condition })),
        group.groups.map((cluster) => cluster.conditions.map((condition) => ({ kind: "clause", condition }))),
      )
    : conditionLines<RowLine>(
        effectiveThreshold == null
          ? [{ kind: "event" }]
          : [{ kind: "threshold" }, ...(window ? [{ kind: "timeWindow" } as const] : [])],
        [],
      )

  const renderLine = (line: RowLine) => {
    switch (line.kind) {
      case "clause": {
        const { condition } = line
        const module = assetNames[condition.targetAssetId]
        return (
          <HStack gap={3}>
            <LockedBox w={FIELD_WIDTH}>
              {module ? `${module} · ${fieldLabel(condition.metric)}` : fieldLabel(condition.metric)}
            </LockedBox>
            <LockedBox w={COMPARISON_WIDTH}>{operatorWithSymbol(condition.operator)}</LockedBox>
            <UnitInput
              value={String(condition.threshold)}
              unit={customAlertField(condition.metric)?.unit ?? ""}
              ariaLabel={`Threshold for ${template.name}: ${fieldLabel(condition.metric)}`}
              disabled
            />
          </HStack>
        )
      }
      case "threshold":
        return (
          <HStack gap={3}>
            {/* A rule with no field to name (e.g. rate_of_change) still reads as a comparison */}
            <LockedBox w={FIELD_WIDTH}>{fieldLabel(template.metric) || "Threshold"}</LockedBox>
            <LockedBox w={COMPARISON_WIDTH}>
              {operatorWithSymbol(template.operator) || template.thresholdDirection?.toLowerCase() || "—"}
            </LockedBox>
            <UnitInput
              value={String(effectiveThreshold)}
              unit={unit}
              ariaLabel={`Threshold for ${template.name}`}
              tone={isOverridden ? "userDefined" : "neutral"}
              disabled
            />
            {isOverridden ? (
              <Text fontSize="sm" fontWeight="semibold" color={USER_DEFINED}>
                User-defined
              </Text>
            ) : (
              <Text fontSize="sm" color={MUTED}>
                Default
              </Text>
            )}
          </HStack>
        )
      case "timeWindow":
        return (
          <HStack gap={3}>
            {/* Qualifies the threshold ("5% within 10 min"); it isn't a second condition */}
            <LockedBox w={FIELD_WIDTH}>Within</LockedBox>
            <Box w={COMPARISON_WIDTH} flexShrink={0} />
            <UnitInput
              value={String(window?.value ?? "")}
              unit={window ? TIME_UNITS[window.unit] : ""}
              ariaLabel={`Time window for ${template.name}`}
              disabled
            />
          </HStack>
        )
      case "event":
        return <LockedBox w={`calc(${FIELD_WIDTH} + ${COMPARISON_WIDTH} + 12px)`}>{template.description ?? "Event-based"}</LockedBox>
    }
  }

  return (
    <Tooltip content={LOCKED_REASON} disabled={!isEnabled}>
      <Box
        role="group"
        aria-label="Conditions"
        aria-description={isEnabled ? LOCKED_REASON : undefined}
        w="fit-content"
        maxW="100%"
        overflowX="auto"
      >
        <ConditionGroup
          lines={lines}
          connective={group && lines.length > 1 ? group.connective : null}
          clusterConnectives={group ? group.groups.map((cluster) => cluster.connective) : []}
          fit
          renderLine={(line) => renderLine(line.item)}
        />
      </Box>
    </Tooltip>
  )
}

export default AlertConditionRows
